import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { AppShell } from "@/components/AppShell";
import { TrialBanner } from "@/components/TrialBanner";
import { NextActionHero } from "@/components/NextActionHero";
import { EmptyStartCard } from "@/components/SampleDataCard";
import { NewUserExplainer } from "@/components/NewUserExplainer";
import { CompactStrengthBadge } from "@/components/ProtectionLayer";
import { DeadlineCountdown as DepositDeadlineCompact } from "@/components/DepositDeadline";
import { DocTypeBadge, StatusBadge } from "@/components/Badges";
import { OutcomeBadge } from "@/components/Outcome";
import { PortfolioStrip } from "@/components/PortfolioStrip";
import { PropertyLimitChoiceDialog } from "@/components/PropertyLimitChoiceDialog";
import { PremiumSection } from "@/components/PremiumSection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronDown, Plus, ArrowRight, GitCompare, FileText, PenLine, Loader2, Sparkles, Scale, AlertTriangle, CalendarClock, Archive, Camera } from "lucide-react";

const fmt = (d) => { try { return format(new Date(d), "MMM d, yyyy"); } catch { return ""; } };

function Section({ title, count, open, onToggle, testId, innerRef, children }) {
  return (
    <div ref={innerRef} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
      <button data-testid={testId} onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F8FAFC] transition-colors">
        <span className="font-heading text-lg font-bold text-navy">
          {title}
          {typeof count === "number" && <span className="ml-2 text-sm font-semibold text-[#94A3B8]">{count}</span>}
        </span>
        <ChevronDown size={20} className={`text-[#94A3B8] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-3 border-t border-[#F1F5F9]">{children}</div>}
    </div>
  );
}

const ACTIVITY_ICON = {
  comparison: GitCompare,
  dispute_letter: FileText,
  baseline_signed: PenLine,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { isPremium, openUpgrade } = useTier();
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [activity, setActivity] = useState([]);
  const [action, setAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(true);
  const [actionError, setActionError] = useState(false);
  const [open, setOpen] = useState({ properties: false, activity: false, deadlines: false, archived: false });
  const [upcoming, setUpcoming] = useState([]);
  const [limitChoice, setLimitChoice] = useState(false);
  const propsRef = useRef(null);

  const loadProps = useCallback(async () => {
    try { const { data } = await api.get("/properties"); setProperties(data); }
    catch { toast.error("Failed to load properties"); }
    finally { setLoadingProps(false); }
  }, []);

  const loadUpcoming = useCallback(async () => {
    try { const { data } = await api.get("/my/upcoming"); setUpcoming(data || []); }
    catch (e) { console.error("loadUpcoming failed", e); }
  }, []);

  const loadSecondary = useCallback(async () => {
    try { const { data } = await api.get("/dashboard/secondary"); setActivity(data.recent_activity || []); }
    catch (e) { console.error("loadSecondary failed", e); /* non-blocking */ }
  }, []);

  const loadAction = useCallback(async () => {
    setActionError(false);
    try { const { data } = await api.get("/dashboard/next-action"); setAction(data); }
    catch { setActionError(true); }
    finally { setActionLoading(false); }
  }, []);

  useEffect(() => { loadProps(); loadSecondary(); loadAction(); loadUpcoming(); }, [loadProps, loadSecondary, loadAction, loadUpcoming]);

  // Poll next-action every 60s while the tab is visible.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) loadAction(); }, 60000);
    return () => clearInterval(id);
  }, [loadAction]);

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const viewProperties = () => {
    setOpen((o) => ({ ...o, properties: true }));
    setTimeout(() => propsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const heroPid = action?.property_id;
  const hasRealProperty = properties.some((p) => !p.is_demo && !p.is_sample);
  // Sample-promotion threshold: a non-demo property with >=1 user-created evidence record
  // (photos, or a sealed baseline). Once met, the big sample card is removed from the dashboard.
  const meaningfulUse = properties.some((p) => !p.is_demo && !p.is_sample && ((p.photo_count || 0) > 0 || p.is_locked));
  const hasSealedRecord = properties.some((p) => !p.is_demo && !p.is_sample && p.is_locked);
  const hasAnyProperty = properties.length > 0;
  const realCount = properties.filter((p) => !p.is_demo && !p.is_sample && p.archive_status !== "archived").length;
  const archivedProperties = useMemo(
    () => properties.filter((p) => p.archive_status === "archived" && !p.is_demo && !p.is_sample),
    [properties]
  );
  const firstActivePropertyId = useMemo(() => {
    const active = properties.filter((p) => !p.is_demo && !p.is_sample && p.archive_status !== "archived");
    active.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    return active[0]?.property_id || null;
  }, [properties]);
  const activeProperties = useMemo(
    () => properties
      .filter((p) => !p.is_demo && !p.is_sample && p.archive_status !== "archived")
      .map((p) => ({ property_id: p.property_id, address: p.address })),
    [properties]
  );

  const [sortBy, setSortBy] = useState("deadline");
  const [filterBy, setFilterBy] = useState("all");
  const displayedProperties = useMemo(() => {
    let list = properties.filter((p) => p.archive_status !== "archived");
    if (filterBy !== "all") {
      list = list.filter((p) => {
        const oc = p.outcome?.status || "pending";
        if (filterBy === "locked") return p.is_locked;
        if (filterBy === "in_progress") return !p.is_locked;
        if (filterBy === "withheld") return oc === "withheld";
        if (filterBy === "returned") return oc === "returned_full" || oc === "returned_partial";
        if (filterBy === "pending") return oc === "pending";
        return true;
      });
    }
    const days = (p) => (p.deadline_status?.days_remaining ?? Infinity);
    if (sortBy === "deadline") list.sort((a, b) => days(a) - days(b));
    else if (sortBy === "status") list.sort((a, b) => (a.is_locked === b.is_locked ? 0 : a.is_locked ? 1 : -1));
    else if (sortBy === "outcome") {
      const rank = { withheld: 0, returned_partial: 1, returned_full: 2, pending: 3 };
      list.sort((a, b) => (rank[a.outcome?.status || "pending"] ?? 3) - (rank[b.outcome?.status || "pending"] ?? 3));
    }
    return list;
  }, [properties, sortBy, filterBy]);
  const deadlineProps = properties.filter(
    (p) => !p.is_demo && p.deadline_status?.configured && !p.deadline_status?.deposit_returned && p.property_id !== heroPid
  );

  return (
    <AppShell>
      <TrialBanner />
      {!loadingProps && !meaningfulUse && (
        <NewUserExplainer onStart={() => { if (!isPremium && realCount >= 1) { setLimitChoice(true); return; } navigate("/start"); }} onSample={() => navigate("/demo")} />
      )}
      <div className="flex items-center justify-between gap-3 mb-5">
        {hasAnyProperty && !meaningfulUse ? (
          <button data-testid="view-sample-link" onClick={() => navigate("/demo")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline">
            <Sparkles size={14} /> Explore a sample property
          </button>
        ) : <span />}
        <div className="flex items-center gap-3">
          <Button data-testid="new-property-btn" onClick={() => {
            if (!isPremium && realCount >= 1) { setLimitChoice(true); return; }
            navigate("/start");
          }} className="bg-brand hover:bg-brand-hover text-white rounded-xl h-11 px-5">
            <Plus size={17} className="mr-1.5" /> New property
          </Button>
        </div>
      </div>

      <NextActionHero
        action={action}
        loading={actionLoading}
        error={actionError}
        onRetry={loadAction}
        onViewProperties={viewProperties}
      />

      <PremiumSection firstPropertyId={firstActivePropertyId} />

      {!loadingProps && !hasRealProperty && <EmptyStartCard onSampleLoaded={loadProps} />}

      {!loadingProps && hasRealProperty && !hasSealedRecord && (
        <button data-testid="quick-start-cta" onClick={() => navigate("/quick-start")}
          className="mt-4 w-full flex items-center gap-3 rounded-2xl border border-brand/20 bg-brand/[0.04] px-5 py-4 text-left hover:brightness-[0.99] transition">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Camera size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-navy">Seal your first room in 5 minutes</p>
            <p className="text-xs text-[#64748B] truncate">Start with one room. Add three photos. Create your first timestamped rental record.</p>
          </div>
          <ArrowRight size={18} className="text-brand shrink-0" />
        </button>
      )}

      {upcoming.length > 0 && (
        <button data-testid="upcoming-date-banner" onClick={() => navigate(`/property/${upcoming[0].property_id}`)}
          className="mt-4 w-full flex items-center gap-3 rounded-2xl border border-amber-border bg-amber-soft px-5 py-3.5 text-left hover:brightness-[0.98] transition">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-amber/20 text-amber-ink shrink-0"><CalendarClock size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-navy truncate">
              {upcoming[0].label} {upcoming[0].days_until === 0 ? "is due today" : `due in ${upcoming[0].days_until} day${upcoming[0].days_until === 1 ? "" : "s"}`}
            </p>
            <p className="text-xs text-[#64748B] truncate">{upcoming[0].address}{upcoming.length > 1 ? ` · +${upcoming.length - 1} more upcoming` : ""}</p>
          </div>
          <ArrowRight size={18} className="text-amber-ink shrink-0" />
        </button>
      )}

      {realCount >= 2 && <PortfolioStrip />}

      <div className="mt-8 space-y-3">
        <Section testId="section-properties" title="Your properties" count={displayedProperties.length} open={open.properties} onToggle={() => toggle("properties")} innerRef={propsRef}>
          {loadingProps ? (
            <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : properties.length === 0 ? (
            <div className="py-1">
              <p className="text-sm text-[#64748B]">This is where your documented rentals live. Use the buttons above to add your place or explore the sample.</p>
              <a href="/help#what-is-deposit-defender" data-testid="empty-properties-learn-more" className="mt-2 inline-block text-sm font-medium text-brand hover:underline">Learn how it works →</a>
            </div>
          ) : (
            <div className="space-y-2">
              {realCount >= 2 && (
                <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="portfolio-controls">
                  <select data-testid="portfolio-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    className="rounded-lg border border-[#E2E8F0] bg-white text-sm text-[#334155] px-3 py-1.5">
                    <option value="deadline">Sort: Deadline</option>
                    <option value="status">Sort: Status</option>
                    <option value="outcome">Sort: Outcome</option>
                  </select>
                  <select data-testid="portfolio-filter" value={filterBy} onChange={(e) => setFilterBy(e.target.value)}
                    className="rounded-lg border border-[#E2E8F0] bg-white text-sm text-[#334155] px-3 py-1.5">
                    <option value="all">All</option>
                    <option value="in_progress">In progress</option>
                    <option value="locked">Locked</option>
                    <option value="withheld">Withheld</option>
                    <option value="returned">Returned</option>
                    <option value="pending">Outcome pending</option>
                  </select>
                </div>
              )}
              {displayedProperties.map((p, i) => {
                const oc = p.outcome;
                const showDispute = p.doc_type === "move_out" && (oc?.status === "withheld" || oc?.status === "returned_partial");
                return (
                <div key={p.property_id} data-testid={`dash-property-${i}`}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] hover:border-brand hover:bg-[#F8FAFC] transition-colors px-4 py-3 text-left">
                  <button onClick={() => navigate(`/property/${p.property_id}`)} className="min-w-0 flex-1 text-left" data-testid={`dash-property-open-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <DocTypeBadge docType={p.doc_type} stage={p.documented_stage} testId={`dash-doctype-${i}`} />
                      <StatusBadge isLocked={p.is_locked} photoCount={p.photo_count} testId={`dash-status-${i}`} />
                      {p.is_demo && <span className="text-[11px] font-semibold text-amber-ink">Demo</span>}
                      {p.is_sample && <span data-testid={`dash-sample-${i}`} className="text-[11px] font-semibold text-brand">Sample</span>}
                      <OutcomeBadge outcome={oc} testId={`dash-outcome-${i}`} />
                    </div>
                    <p className="mt-1 font-semibold text-navy truncate">{p.address}</p>
                    <p className="text-xs text-[#94A3B8]">{p.room_count} rooms · {p.photo_count} photos</p>
                    {!p.is_locked && p.flagged_count > 0 && (
                      <span data-testid={`dash-retake-badge-${i}`}
                        className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-border bg-amber-soft px-2 py-0.5 text-[11px] font-semibold text-amber-ink">
                        <AlertTriangle size={11} /> {p.flagged_count} photo{p.flagged_count > 1 ? "s" : ""} need a retake
                      </span>
                    )}
                    <CompactStrengthBadge propertyId={p.property_id} testId={`dash-strength-${i}`} />
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    {showDispute && (
                      <Button data-testid={`dash-dispute-${i}`} size="sm" variant="outline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.property_id}/dispute-letter`); }}
                        className="rounded-lg border-[#FECACA] text-[#B91C1C] hover:bg-[#FEE2E2] hidden sm:inline-flex">
                        <Scale size={14} className="mr-1.5" /> Dispute letter
                      </Button>
                    )}
                    <ArrowRight size={16} className="text-[#CBD5E1]" />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </Section>

        {archivedProperties.length > 0 && (
          <Section testId="section-archived" title="Archived" count={archivedProperties.length} open={open.archived} onToggle={() => toggle("archived")}>
            <div className="space-y-2">
              {archivedProperties.map((p, i) => (
                <button key={p.property_id} data-testid={`dash-archived-${i}`}
                  onClick={() => navigate(`/property/${p.property_id}`)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] hover:border-brand hover:bg-[#F8FAFC] transition-colors px-4 py-3 text-left">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <DocTypeBadge docType={p.doc_type} stage={p.documented_stage} testId={`dash-archived-doctype-${i}`} />
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] px-2 py-0.5 text-[11px] font-semibold text-[#64748B]">
                        <Archive size={11} /> Archived
                      </span>
                    </div>
                    <p className="mt-1 font-semibold text-navy truncate">{p.address}</p>
                    {p.archived_at && <p className="text-xs text-[#94A3B8]">Archived {format(new Date(p.archived_at), "MMM d, yyyy")}</p>}
                  </div>
                  <ArrowRight size={16} className="text-[#CBD5E1]" />
                </button>
              ))}
            </div>
          </Section>
        )}

        <Section testId="section-activity" title="Recent activity" count={activity.length} open={open.activity} onToggle={() => toggle("activity")}>
          {activity.length === 0 ? (
            <p className="text-sm text-[#64748B] py-1">Nothing yet.</p>
          ) : (
            <ul className="space-y-2">
              {activity.map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || FileText;
                return (
                  <li key={i} data-testid={`activity-${i}`} className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] px-4 py-3">
                    <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#F1F5F9] text-navy shrink-0"><Icon size={16} /></span>
                    <div className="min-w-0">
                      <p className="font-semibold text-navy text-sm">{a.label}</p>
                      <p className="text-xs text-[#94A3B8] truncate">{a.property_address} · {fmt(a.timestamp)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section testId="section-deadlines" title="Deadlines" count={deadlineProps.length} open={open.deadlines} onToggle={() => toggle("deadlines")}>
          {deadlineProps.length === 0 ? (
            <p className="text-sm text-[#64748B] py-1">No active deadlines to track.</p>
          ) : (
            <div className="space-y-3">
              {deadlineProps.map((p) => (
                <button key={p.property_id} data-testid={`dash-deadline-${p.property_id}`} onClick={() => navigate(`/property/${p.property_id}`)} className="block w-full text-left">
                  <p className="text-xs font-semibold text-navy truncate">{p.address}</p>
                  <DepositDeadlineCompact variant="compact" status={p.deadline_status} address={p.address} />
                </button>
              ))}
            </div>
          )}
        </Section>
      </div>

      <PropertyLimitChoiceDialog
        open={limitChoice}
        onOpenChange={setLimitChoice}
        activeProperties={activeProperties}
        onView={(id) => { setLimitChoice(false); if (id) navigate(`/property/${id}`); }}
        onUpgrade={() => { setLimitChoice(false); openUpgrade("multiple_properties"); }}
        onArchive={(id) => { setLimitChoice(false); if (id) navigate(`/property/${id}?archive=1`); }}
        onManageAll={() => { setLimitChoice(false); viewProperties(); }}
      />
    </AppShell>
  );
}
